import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { trackOrder } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, ChefHat, Bell, Package } from 'lucide-react';

const API = `${window.location.origin}/api`;

const STEPS = [
    { key: 'new', label: 'Order Placed', icon: Package, color: '#6366F1' },
    { key: 'preparing', label: 'Preparing', icon: ChefHat, color: '#F59E0B' },
    { key: 'ready', label: 'Ready', icon: Bell, color: '#22C55E' },
    { key: 'done', label: 'Completed', icon: CheckCircle2, color: '#8B5CF6' },
];

function getStepIndex(status) {
    const idx = STEPS.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
}

export default function OrderTracking() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [restaurant, setRestaurant] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const eventSourceRef = useRef(null);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const res = await trackOrder(orderId);
                setOrder(res.data.order);
                setRestaurant(res.data.restaurant || {});
            } catch {
                setError('Order not found');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId]);

    // SSE for live updates
    useEffect(() => {
        if (!order) return;
        let aborted = false;
        const es = new EventSource(`${API}/track/${orderId}/stream`);
        eventSourceRef.current = es;

        es.onmessage = (event) => {
            if (aborted) return;
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'status_update' && data.order) {
                    setOrder(data.order);
                }
            } catch { /* ignore parse errors */ }
        };

        es.onerror = () => {
            if (!aborted) {
                es.close();
                // Reconnect after 5s
                setTimeout(() => {
                    if (!aborted) {
                        const newEs = new EventSource(`${API}/track/${orderId}/stream`);
                        eventSourceRef.current = newEs;
                        newEs.onmessage = es.onmessage;
                    }
                }, 5000);
            }
        };

        return () => {
            aborted = true;
            es.close();
        };
    }, [order?.restaurant_id, orderId, order]);

    const primaryColor = restaurant.primary_color || '#C9A227';
    const currentStep = order ? getStepIndex(order.status) : 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-[#C9A227] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center text-white">
                <div className="text-center">
                    <Package className="w-16 h-16 mx-auto mb-4 text-white/20" />
                    <h1 className="text-xl font-medium mb-2">Order Not Found</h1>
                    <p className="text-white/40 text-sm">The order ID may be incorrect or expired.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="order-tracking-page">
            {/* Header */}
            <div className="text-center pt-10 pb-6 px-5">
                {restaurant.logo_url ? (
                    <img src={restaurant.logo_url} alt="" className="w-14 h-14 object-contain mx-auto mb-3 rounded-xl" />
                ) : (
                    <div className="w-14 h-14 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                        <ChefHat className="w-7 h-7 text-white" />
                    </div>
                )}
                <h1 className="text-lg font-medium" style={{ color: primaryColor }}>{restaurant.name || 'Restaurant'}</h1>
                <p className="text-white/30 text-xs mt-1">Order Tracking</p>
            </div>

            <div className="max-w-md mx-auto px-5 pb-20">
                {/* Order ID Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl p-5 mb-8"
                    style={{ backgroundColor: `${primaryColor}10`, border: `1px solid ${primaryColor}30` }}
                >
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: `${primaryColor}80` }}>Order ID</p>
                    <p className="text-2xl font-mono font-bold tracking-wider" style={{ color: primaryColor }} data-testid="tracking-order-id">
                        #{order.id}
                    </p>
                    {order.customer_name && <p className="text-sm text-white/40 mt-1">{order.customer_name}</p>}
                    {order.table && <p className="text-xs text-white/30 mt-0.5">Table {order.table}</p>}
                </motion.div>

                {/* Status Tracker */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mb-8"
                    data-testid="status-tracker"
                >
                    <div className="relative">
                        {STEPS.map((step, i) => {
                            const Icon = step.icon;
                            const isActive = i <= currentStep;
                            const isCurrent = i === currentStep;
                            return (
                                <div key={step.key} className="flex items-start gap-4 relative" data-testid={`step-${step.key}`}>
                                    {/* Vertical line */}
                                    {i < STEPS.length - 1 && (
                                        <div className="absolute left-5 top-10 w-0.5 h-12" style={{ backgroundColor: i < currentStep ? step.color : 'rgba(255,255,255,0.08)' }} />
                                    )}
                                    {/* Icon circle */}
                                    <motion.div
                                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 relative z-10"
                                        style={{
                                            backgroundColor: isActive ? step.color : 'rgba(255,255,255,0.05)',
                                            boxShadow: isCurrent ? `0 0 20px ${step.color}40` : 'none'
                                        }}
                                        animate={isCurrent ? { scale: [1, 1.1, 1] } : {}}
                                        transition={{ repeat: Infinity, duration: 2 }}
                                    >
                                        <Icon className="w-5 h-5" style={{ color: isActive ? 'white' : 'rgba(255,255,255,0.2)' }} />
                                    </motion.div>
                                    {/* Text */}
                                    <div className="pt-2 pb-10">
                                        <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-white/30'}`}>
                                            {step.label}
                                        </p>
                                        {isCurrent && (
                                            <motion.p
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-xs mt-0.5"
                                                style={{ color: `${step.color}CC` }}
                                            >
                                                {step.key === 'new' && 'Your order has been received'}
                                                {step.key === 'preparing' && 'The kitchen is working on your order'}
                                                {step.key === 'ready' && 'Your order is ready for pickup!'}
                                                {step.key === 'done' && 'Enjoy your meal!'}
                                            </motion.p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Order Items */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Order Items</p>
                    <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                        {(order.items || []).map((item, i) => (
                            <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04] last:border-0">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-white/30 w-5">{item.qty}x</span>
                                    <span className="text-sm text-white/80">{item.name}</span>
                                </div>
                                <span className="text-sm" style={{ color: primaryColor }}>QAR {(item.qty * item.price).toFixed(0)}</span>
                            </div>
                        ))}
                        <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                            <span className="text-sm font-medium text-white/60">Total</span>
                            <span className="text-lg font-bold" style={{ color: primaryColor }}>QAR {order.total}</span>
                        </div>
                    </div>
                </motion.div>

                {/* Live indicator */}
                <div className="mt-6 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.06]">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                        </span>
                        <span className="text-[10px] text-white/30 uppercase tracking-wider">Live tracking</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
